import {
	FC,
	useState,
	ChangeEvent,
	useLayoutEffect,
	useMemo,
	useCallback,
	useReducer,
	useEffect,
} from 'react';
import { Loading, Modal, Skeleton, Table } from '../../../../components';
import { RegistrationPage } from '../../templates';
import { RegistrationEntities, tiposDeVeiculo } from '../../../../utils';
import {
	FormFilter,
	NoData,
	OneColumn,
	TableWrapper,
} from '../../Registration.styles';
import {
	TVeiculoAno,
	getVehicleBrands,
	getVehicleModels,
	getVehicleYears,
} from '../../../../services';
import { useToastStore } from '../../../../stores';
import Dropdown, { Option } from '../../../../components/dropdown/Dropdown';

export type State = {
	idVeiculoMarca: number;
	idVeiculoModelo: number;
	idVeiculoTipo: number;
};

export const yearInitialState: State = {
	idVeiculoMarca: 0,
	idVeiculoModelo: 0,
	idVeiculoTipo: 0,
};

type Action = { type: 'SET_SELECT_OPTION'; field: string; value: string | number };

export const offerReducer = (state: State, action: Action) => {
	return {
		...state,
		[action.field]: action.value,
	};
};

const RegistrationYears: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState<boolean>(false);
	const [fakeLoading, setFakeLoading] = useState(false);
	const [vehicleYears, setVehicleYears] = useState<TVeiculoAno[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [state, dispatch] = useReducer(offerReducer, yearInitialState);
	const [marcasDeVeiculo, setMarcasDeVeiculo] = useState<Option[]>([]);
	const [procurandoMarcas, setProcurandoMarcas] = useState<boolean>(false);
	const [modelosDeVeiculo, setModelosDeVeiculo] = useState<Option[]>([]);
	const [procurandoModelos, setProcurandoModelos] = useState<boolean>(false);

	const tableColumns = useMemo(
		() => [
			{ id: 'year', label: 'Ano' },
			{ id: 'fipe', label: 'FIPE' },
			{ id: 'reference', label: 'Referência' },
			{ id: 'fipeValue', label: 'Valor FIPE' },
			//{ id: 'actions', label: 'Ações' },
		],
		[]
	);

	const tipoDeVeiculoSelecionado = useMemo(
		() => tiposDeVeiculo.find((veiculo) => veiculo.value === state.idVeiculoTipo),
		[state.idVeiculoTipo]
	);
	const marcaSelecionada = useMemo(
		() => marcasDeVeiculo.find((marca) => marca.value === state.idVeiculoMarca),
		[marcasDeVeiculo, state.idVeiculoMarca]
	);
	const modeloSelecionado = useMemo(
		() => modelosDeVeiculo.find((modelo) => modelo.value === state.idVeiculoModelo),
		[modelosDeVeiculo, state.idVeiculoModelo]
	);

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
	};

	const rowMapper = useCallback(
		(item: TVeiculoAno) => ({
			year: { value: item.veiculoAno, rawValue: item.veiculoAno },
			fipe: { value: item.codigoFipe, rawValue: item.codigoFipe },
			reference: { value: item.mesAnoReferencia, rawValue: item.mesAnoReferencia },
			fipeValue: { value: item.valorFipe, rawValue: item.valorFipe },
			/*
			actions: {
				value: (
					<ActionsContainer>
						<Tooltip title='Editar' placement='top' arrow>
							<Link to=''>
								<Button variant='icon' icon={<EditOutlined fontSize='inherit' />} />
							</Link>
						</Tooltip>
						<Tooltip title='Excluir' placement='top' arrow>
							<div>
								<Button
									variant='icon'
									icon={<DeleteOutlined fontSize='inherit' />}
									onClick={() => setOpenDeleteModal(true)}
								/>
							</div>
						</Tooltip>
					</ActionsContainer>
				),
			},
			*/
		}),
		[]
	);

	const resetSelectedOptions = (fields: string[]) => {
		fields.forEach((field) => {
			dispatch({
				type: 'SET_SELECT_OPTION',
				field: field,
				value: 0,
			});
		});
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
		handleSearchSubmit();
	};

	const handleSearchSubmit = () => {
		setFakeLoading(true);

		const filtered = vehicleYears?.filter(
			(vehicleYear) =>
				vehicleYear.mesAnoReferencia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				vehicleYear.codigoFipe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				vehicleYear.veiculoAno?.toLowerCase().includes(searchTerm.toLowerCase())
		);

		setTimeout(() => setVehicleYears(filtered), 100);
		setTimeout(() => setFakeLoading(false), 1000);
	};

	useEffect(() => {
		const fetchMarcas = async () => {
			if (!state.idVeiculoTipo) return;

			setProcurandoMarcas(true);
			setProcurandoModelos(true);

			try {
				const response = await getVehicleBrands(state.idVeiculoTipo);

				const opcoesDeMarca: Option[] = response.map((marca) => ({
					value: marca.id,
					label: marca.veiculoMarca ?? '',
				}));
				opcoesDeMarca.sort((a, b) => a.label.localeCompare(b.label));

				setMarcasDeVeiculo(opcoesDeMarca);

				const opcoesContemMarcaSelecionada =
					opcoesDeMarca.findIndex((marca) => marca.value === state.idVeiculoMarca) !== -1;

				if (!opcoesContemMarcaSelecionada) {
					resetSelectedOptions(['idVeiculoMarca', 'idVeiculoModelo']);
					setModelosDeVeiculo([]);
				}
			} catch {
				setToast({ message: 'Erro ao buscar marcas de veículo.', variant: 'error' });
			} finally {
				setTimeout(() => {
					setProcurandoMarcas(false);
					setProcurandoModelos(false);
				}, 500);
			}
		};

		fetchMarcas();
	}, [setToast, state.idVeiculoMarca, state.idVeiculoTipo]);

	useEffect(() => {
		const fetchModelos = async () => {
			if (!state.idVeiculoMarca) return;

			setProcurandoModelos(true);

			try {
				const response = await getVehicleModels(state.idVeiculoMarca);

				const opcoesDeModelo: Option[] = response.map((modelo) => ({
					value: modelo.id,
					label: modelo.veiculoModelo ?? '',
				}));
				opcoesDeModelo.sort((a, b) => a.label.localeCompare(b.label));

				setModelosDeVeiculo(opcoesDeModelo);

				const opcoesContemModeloSelecionado =
					opcoesDeModelo.findIndex((modelo) => modelo.value === state.idVeiculoModelo) !== -1;

				if (!opcoesContemModeloSelecionado) {
					resetSelectedOptions(['idVeiculoModelo']);
				}
			} catch {
				setToast({ message: 'Erro ao buscar modelos de veículo.', variant: 'error' });
			} finally {
				setTimeout(() => {
					setProcurandoModelos(false);
				}, 500);
			}
		};

		fetchModelos();
	}, [setToast, state.idVeiculoMarca, state.idVeiculoModelo]);

	const handleDelete = () => {};

	const MemoizedTable = useMemo(
		() => <Table paginate columns={tableColumns} rows={vehicleYears.map(rowMapper)} />,
		[vehicleYears, rowMapper, tableColumns]
	);

	useLayoutEffect(() => {
		if (!!state.idVeiculoModelo) {
			const fetchData = async () => {
				setLoading(true);
				try {
					const response = await getVehicleYears(state.idVeiculoModelo);
					setVehicleYears(response || []);
				} catch {
					setToast({
						message: 'Erro ao buscar os anos dos modelos!',
						variant: 'error',
					});
				} finally {
					setTimeout(() => setLoading(false), 500);
				}
			};

			fetchData();
		}
	}, [setToast, state.idVeiculoModelo]);

	if (loading) return <Loading fullScreen />;

	return (
		<RegistrationPage
			entity={RegistrationEntities.year}
			searchTerm={searchTerm}
			disableNewButton={true}
			onChange={handleSearchChange}
		>
			<>
				<Modal
					open={openDeleteModal}
					onClose={() => setOpenDeleteModal(false)}
					title='Confirmação!'
					primaryButtonText='Sim'
					secondaryButtonText='Não'
					onPrimaryButtonClick={handleDelete}
					onSecondaryButtonClick={() => setOpenDeleteModal(false)}
				>
					Deseja realmente excluir?
				</Modal>
				<FormFilter>
					<OneColumn>
						<Dropdown
							options={tiposDeVeiculo}
							selected={tipoDeVeiculoSelecionado}
							onSelect={(option: Option) => handleSelectOption('idVeiculoTipo', option)}
						/>
					</OneColumn>
					<OneColumn>
						{procurandoMarcas ? (
							<Skeleton $borderRadius={12} />
						) : (
							<Dropdown
								searchable
								options={marcasDeVeiculo}
								selected={marcaSelecionada}
								onSelect={(option: Option) => handleSelectOption('idVeiculoMarca', option)}
								disabled={marcasDeVeiculo.length === 0}
							/>
						)}
					</OneColumn>
					<OneColumn>
						{procurandoModelos ? (
							<Skeleton $borderRadius={12} />
						) : (
							<Dropdown
								searchable
								options={modelosDeVeiculo}
								selected={modeloSelecionado}
								onSelect={(option: Option) => handleSelectOption('idVeiculoModelo', option)}
								disabled={modelosDeVeiculo.length === 0}
							/>
						)}
					</OneColumn>
				</FormFilter>
				{!loading && vehicleYears.length > 0 && (
					<TableWrapper>{fakeLoading ? <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && vehicleYears.length === 0 && <NoData>Nenhum registro encontrado</NoData>}
			</>
		</RegistrationPage>
	);
};

export default RegistrationYears;
